import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Spinner, Modal, Button, Form } from "react-bootstrap";
import { Icon } from "../../utils/iconHelper";
import ProgramManagementService, {
  ProgramDetail,
  ProgramLookups,
} from "../../_services/ProgramManagementService";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./program-management.css";

const ProgramViewPage: React.FC = () => {
  useDocumentTitle("Program Detail");
  const history = useHistory();
  const { programId } = useParams<{ programId: string }>();
  const id = Number(programId);

  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [lookups, setLookups] = useState<ProgramLookups>({ years: [], terms: [], courses: [], schools: [], programTypes: [], streams: [], testCategories: [], universities: [], learningOptions: [], disciplines: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showMapIntake, setShowMapIntake] = useState(false);
  const [showAddTerm, setShowAddTerm] = useState(false);
  const [showAddModule, setShowAddModule] = useState(false);

  const [selectedIntakeYearId, setSelectedIntakeYearId] = useState<number | null>(null);
  const [intakeModules, setIntakeModules] = useState<any[]>([]);
  const [intakeModulesLoading, setIntakeModulesLoading] = useState(false);
  const [termDatesByTermId, setTermDatesByTermId] = useState<Map<number, { startDate: string | null; endDate: string | null }>>(new Map());

  useEffect(() => {
    loadAll();
  }, [id]);

  // Auto-select the first intake once the program loads so its modules are
  // shown without requiring a manual click. Without this, a program (often
  // single-intake) shows the "Click an intake above to see its modules"
  // placeholder even when mappings exist — making them look missing while
  // Add Module still rejects them as "already mapped".
  useEffect(() => {
    if (program && program.intakes.length > 0 && selectedIntakeYearId == null) {
      const first = program.intakes[0];
      setSelectedIntakeYearId(first.yearId);
      loadModulesForIntake(first.yearId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program]);

  const loadAll = async () => {
    setLoading(true);
    const [p, l] = await Promise.all([
      ProgramManagementService.getProgramById(id),
      ProgramManagementService.getLookups(),
    ]);
    if (p.success && p.data) {
      setProgram(p.data);
    } else {
      setError(p.message || "Failed to load program");
    }
    if (l.success) setLookups(l.data);
    setLoading(false);
  };

  const intakeIds = useMemo(() => new Set((program?.intakes ?? []).map((i) => i.yearId)), [program]);
  const availableYears = useMemo(
    () => lookups.years.filter((y) => !intakeIds.has(y.id)),
    [lookups.years, intakeIds]
  );

  const modulesByIntake = useMemo(() => {
    const grouped = new Map<number, typeof program.modules>();
    (program?.modules ?? []).forEach((m) => {
      if (m.disableStatus === 1) return;
      if (m.yearId == null) return;
      if (!grouped.has(m.yearId)) grouped.set(m.yearId, []);
      grouped.get(m.yearId)!.push(m);
    });
    return grouped;
  }, [program]);

  const loadModulesForIntake = async (yearId: number) => {
    setIntakeModulesLoading(true);
    const r = await ProgramManagementService.getModulesByIntake(id, yearId);
    if (r.success && r.data) {
      setIntakeModules(r.data.modules);
      const map = new Map<number, { startDate: string | null; endDate: string | null }>();
      (r.data.termDates || []).forEach((t: any) => {
        map.set(t.termId, { startDate: t.startDate, endDate: t.endDate });
      });
      setTermDatesByTermId(map);
    } else {
      setIntakeModules([]);
      setTermDatesByTermId(new Map());
      setError(r.message || "Failed to load modules for intake");
    }
    setIntakeModulesLoading(false);
  };

  const handleIntakeClick = (yearId: number) => {
    if (selectedIntakeYearId === yearId) {
      // toggle off
      setSelectedIntakeYearId(null);
      setIntakeModules([]);
      setTermDatesByTermId(new Map());
      return;
    }
    setSelectedIntakeYearId(yearId);
    loadModulesForIntake(yearId);
  };

  if (loading) {
    return (
      <div className="pm-page">
        <div className="pm-loading">
          <Spinner animation="border" variant="primary" role="status" />
          <p>Loading program details...</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="pm-page">
        <button className="pm-back-link" onClick={() => history.push("/program-management")}>
          <Icon.ArrowLeft size={14} /> Back to programs
        </button>
        <Alert variant="danger">{error || "Program not found"}</Alert>
      </div>
    );
  }

  return (
    <div className="pm-page">
      <button className="pm-back-link" onClick={() => history.push("/program-management")}>
        <Icon.ArrowLeft size={14} /> Back to programs
      </button>

      <div className="pm-header">
        <div>
          <h1>{program.name}</h1>
          <div className="pm-meta-row">
            {program.programShortCode && <span>Code: <strong>{program.programShortCode}</strong></span>}
            {program.noOfAcademicYears != null && <span>{program.noOfAcademicYears} year(s)</span>}
            <span>Status: {program.enableStatus === 1 ? "Active" : "Inactive"}</span>
            <span>Registration: {program.openForRegistration === 1 ? "Open" : "Closed"}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="pm-alert pm-alert-error">
          <span>{error}</span>
          <button className="pm-alert-dismiss" onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="pm-alert pm-alert-success">
          <span>{success}</span>
          <button className="pm-alert-dismiss" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {/* Intakes */}
      <div className="pm-section">
        <div className="pm-section-header">
          <h3>Intakes ({program.intakes.length})</h3>
          <button className="pm-btn-action primary" onClick={() => setShowMapIntake(true)}>
            <Icon.Plus size={14} /> Map Intake
          </button>
        </div>
        {program.intakes.length === 0 ? (
          <div className="pm-cell-muted">No intakes mapped yet.</div>
        ) : (
          <div className="pm-intake-grid">
            {program.intakes.map((intake) => {
              const mods = modulesByIntake.get(intake.yearId) || [];
              const isSelected = selectedIntakeYearId === intake.yearId;
              return (
                <div
                  key={intake.mappingId}
                  className="pm-intake-card"
                  onClick={() => handleIntakeClick(intake.yearId)}
                  style={{
                    cursor: "pointer",
                    borderColor: isSelected ? "#2563eb" : undefined,
                    boxShadow: isSelected ? "0 0 0 2px rgba(37, 99, 235, 0.15)" : undefined,
                  }}
                  title="Click to view modules for this intake"
                >
                  <div className="pm-intake-card-name">{intake.yearName}</div>
                  {intake.yearCode && <div className="pm-intake-card-meta">Code: {intake.yearCode}</div>}
                  <div className="pm-intake-card-meta">
                    Reg: {intake.openForRegistration === 1 ? "Open" : "Closed"} • {mods.length} module(s)
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Terms */}
      <div className="pm-section">
        <div className="pm-section-header">
          <h3>
            Terms ({program.terms.length})
            {selectedIntakeYearId != null && (
              <span style={{ fontSize: 14, fontWeight: "normal", color: "var(--text-muted)", marginLeft: 8 }}>
                — dates for {program.intakes.find((i) => i.yearId === selectedIntakeYearId)?.yearName}
              </span>
            )}
          </h3>
          <button className="pm-btn-action primary" onClick={() => setShowAddTerm(true)}>
            <Icon.Plus size={14} /> Add Term
          </button>
        </div>
        {program.terms.length === 0 ? (
          <div className="pm-cell-muted">No terms added yet.</div>
        ) : (
          <table className="pm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Display Name</th>
                <th style={{ width: 130 }}>Start Date</th>
                <th style={{ width: 130 }}>End Date</th>
              </tr>
            </thead>
            <tbody>
              {program.terms.map((t) => {
                const dates = termDatesByTermId.get(t.id);
                const startDate = dates?.startDate ?? t.startDate;
                const endDate = dates?.endDate ?? t.endDate;
                return (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="pm-cell-muted">{t.displayName || "—"}</td>
                    <td className="pm-cell-muted">{startDate ? String(startDate).slice(0, 10) : "—"}</td>
                    <td className="pm-cell-muted">{endDate ? String(endDate).slice(0, 10) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modules for selected intake */}
      <div className="pm-section">
        <div className="pm-section-header">
          <h3>
            Modules / Courses
            {selectedIntakeYearId != null && (
              <span style={{ fontSize: 14, fontWeight: "normal", color: "var(--text-muted)", marginLeft: 8 }}>
                — {program.intakes.find((i) => i.yearId === selectedIntakeYearId)?.yearName}
              </span>
            )}
          </h3>
          <button
            className="pm-btn-action primary"
            onClick={() => setShowAddModule(true)}
            disabled={program.intakes.length === 0}
          >
            <Icon.Plus size={14} /> Add Module
          </button>
        </div>
        {program.intakes.length === 0 ? (
          <div className="pm-cell-muted">Map at least one intake before adding modules.</div>
        ) : selectedIntakeYearId == null ? (
          <div className="pm-cell-muted">Click an intake above to see its modules.</div>
        ) : intakeModulesLoading ? (
          <div className="pm-loading"><Spinner animation="border" size="sm" /> Loading modules...</div>
        ) : intakeModules.length === 0 ? (
          <div className="pm-cell-muted">No modules for this intake.</div>
        ) : (
          <table className="pm-table">
            <thead>
              <tr>
                <th>Course</th>
                <th style={{ width: 110 }}>Code</th>
                <th style={{ width: 140 }}>Term</th>
                <th style={{ width: 80 }}>Credits</th>
                <th style={{ width: 100 }}>Local Fee</th>
                <th style={{ width: 130 }}>Intl Fee</th>
                <th style={{ width: 110 }}>Start Date</th>
                <th style={{ width: 110 }}>End Date</th>
                <th style={{ width: 120 }}>Exam Start</th>
                <th style={{ width: 120 }}>Exam End</th>
                <th style={{ width: 70, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {intakeModules.map((m) => (
                <tr key={m.mappingId}>
                  <td>{m.courseName || "—"}</td>
                  <td className="pm-cell-muted">{m.courseShortCode || "—"}</td>
                  <td className="pm-cell-muted">{m.termDisplayName || m.termName || "—"}</td>
                  <td className="pm-cell-muted">{m.credits ?? "—"}</td>
                  <td className="pm-cell-muted">{m.localFee != null ? m.localFee : "—"}</td>
                  <td className="pm-cell-muted">{m.internationalFee != null ? m.internationalFee : "—"}</td>
                  <td className="pm-cell-muted">{m.startDate ? String(m.startDate).slice(0, 10) : "—"}</td>
                  <td className="pm-cell-muted">{m.endDate ? String(m.endDate).slice(0, 10) : "—"}</td>
                  <td className="pm-cell-muted">{m.examStartDate ? String(m.examStartDate).slice(0, 10) : "—"}</td>
                  <td className="pm-cell-muted">{m.examEndDate ? String(m.examEndDate).slice(0, 10) : "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="pm-btn-action"
                      onClick={async () => {
                        const r = await ProgramManagementService.removeModule(id, m.mappingId);
                        if (r.success) {
                          setSuccess("Module removed");
                          if (selectedIntakeYearId != null) loadModulesForIntake(selectedIntakeYearId);
                          loadAll();
                        } else {
                          setError(r.message || "Failed to remove module");
                        }
                      }}
                      title="Remove module"
                    >
                      <Icon.Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MapIntakeModal
        show={showMapIntake}
        onHide={() => setShowMapIntake(false)}
        availableYears={availableYears}
        onSubmit={async (body) => {
          const r = await ProgramManagementService.mapIntake(id, body);
          if (r.success) { setSuccess("Intake mapped"); setShowMapIntake(false); loadAll(); }
          else setError(r.message || "Failed to map intake");
        }}
      />
      <AddTermModal
        show={showAddTerm}
        onHide={() => setShowAddTerm(false)}
        intakes={program.intakes}
        onSubmit={async (body) => {
          const r = await ProgramManagementService.addTerm(id, body);
          if (r.success) { setSuccess("Term added"); setShowAddTerm(false); loadAll(); }
          else setError(r.message || "Failed to add term");
        }}
      />
      <AddModuleModal
        show={showAddModule}
        onHide={() => setShowAddModule(false)}
        intakes={program.intakes}
        terms={program.terms}
        courses={lookups.courses}
        existingCourseIds={intakeModules.map((m) => m.courseId).filter((c) => c != null) as number[]}
        defaultYearId={selectedIntakeYearId}
        onSubmit={async (body) => {
          const r = await ProgramManagementService.addModule(id, body);
          if (r.success) { setSuccess("Module added"); setShowAddModule(false); loadAll(); }
          else setError(r.message || "Failed to add module");
        }}
      />
    </div>
  );
};

// --- Map Intake Modal ---
const MapIntakeModal: React.FC<{
  show: boolean;
  onHide: () => void;
  availableYears: { id: number; name: string }[];
  onSubmit: (body: { yearId: number; intakeDisplayName?: string | null; openForRegistration?: number }) => Promise<void>;
}> = ({ show, onHide, availableYears, onSubmit }) => {
  const [yearId, setYearId] = useState<string>("");
  const [intakeDisplayName, setIntakeDisplayName] = useState("");
  const [openForRegistration, setOpenForRegistration] = useState<number>(0);

  useEffect(() => {
    if (show) {
      setYearId("");
      setIntakeDisplayName("");
      setOpenForRegistration(0);
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered className="pm-modal" dialogClassName="pm-modal-dialog">
      <Modal.Header closeButton><Modal.Title>Map Intake</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Year (Intake) *</Form.Label>
          <Form.Select value={yearId} onChange={(e) => setYearId(e.target.value)}>
            <option value="">-- Select --</option>
            {availableYears.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Intake Display Name</Form.Label>
          <Form.Control type="text" value={intakeDisplayName} onChange={(e) => setIntakeDisplayName(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Open For Registration</Form.Label>
          <Form.Select value={openForRegistration} onChange={(e) => setOpenForRegistration(Number(e.target.value))}>
            <option value={0}>No</option>
            <option value={1}>Yes</option>
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button
          variant="primary"
          disabled={!yearId}
          onClick={() => onSubmit({ yearId: Number(yearId), intakeDisplayName: intakeDisplayName || null, openForRegistration })}
        >
          Map Intake
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// --- Add Term Modal ---
const AddTermModal: React.FC<{
  show: boolean;
  onHide: () => void;
  intakes: { yearId: number; yearName: string | null }[];
  onSubmit: (body: { name: string; displayName?: string | null; startDate?: string | null; endDate?: string | null; yearId?: number | null }) => Promise<void>;
}> = ({ show, onHide, intakes, onSubmit }) => {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [yearId, setYearId] = useState("");

  useEffect(() => {
    if (show) { setName(""); setDisplayName(""); setStartDate(""); setEndDate(""); setYearId(""); }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered className="pm-modal" dialogClassName="pm-modal-dialog">
      <Modal.Header closeButton><Modal.Title>Add Term</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Term Name *</Form.Label>
          <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Display Name</Form.Label>
          <Form.Control type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Form.Group>
        <div className="row mb-3">
          <div className="col-6">
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="col-6">
            <Form.Label>End Date</Form.Label>
            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Map to Intake (optional)</Form.Label>
          <Form.Select value={yearId} onChange={(e) => setYearId(e.target.value)}>
            <option value="">-- None --</option>
            {intakes.map((i) => <option key={i.yearId} value={i.yearId}>{i.yearName}</option>)}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button
          variant="primary"
          disabled={!name.trim()}
          onClick={() => onSubmit({
            name,
            displayName: displayName || null,
            startDate: startDate || null,
            endDate: endDate || null,
            yearId: yearId ? Number(yearId) : null,
          })}
        >
          Add Term
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// --- Add Module Modal ---
// The intake is already known from the page (shown read-only). The Course dropdown
// lists the modules/courses that can still be added — courses already mapped to this
// intake are filtered out so you can't add a duplicate.
const AddModuleModal: React.FC<{
  show: boolean;
  onHide: () => void;
  intakes: { yearId: number; yearName: string | null }[];
  terms: { id: number; name: string | null; displayName: string | null }[];
  courses: { id: number; name: string }[];
  existingCourseIds: number[];
  defaultYearId: number | null;
  onSubmit: (body: { courseId: number; yearId: number; termId?: number | null; credits?: number | null; startDate?: string | null; endDate?: string | null }) => Promise<void>;
}> = ({ show, onHide, intakes, terms, courses, existingCourseIds, defaultYearId, onSubmit }) => {
  const [courseId, setCourseId] = useState("");
  const [yearId, setYearId] = useState("");
  const [termId, setTermId] = useState("");
  const [credits, setCredits] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const knownIntake = defaultYearId != null;
  const intakeName = intakes.find((i) => i.yearId === defaultYearId)?.yearName ?? null;

  // Only offer courses that aren't already added as modules for this intake.
  const availableCourses = useMemo(() => {
    const taken = new Set(existingCourseIds);
    return courses.filter((c) => !taken.has(c.id));
  }, [courses, existingCourseIds]);

  useEffect(() => {
    if (show) {
      setCourseId("");
      setYearId(defaultYearId != null ? String(defaultYearId) : "");
      setTermId("");
      setCredits("");
      setStartDate("");
      setEndDate("");
    }
  }, [show, defaultYearId]);

  return (
    <Modal show={show} onHide={onHide} centered className="pm-modal" dialogClassName="pm-modal-dialog">
      <Modal.Header closeButton><Modal.Title>Add Module</Modal.Title></Modal.Header>
      <Modal.Body>
        {intakeName && (
          <div className="pm-modal-context">
            <div className="pm-modal-context-item">
              <span className="pm-modal-context-label">Intake</span>
              <span className="pm-modal-context-value">{intakeName}</span>
            </div>
          </div>
        )}
        {!knownIntake && (
          <Form.Group className="mb-3">
            <Form.Label>Intake (Year) *</Form.Label>
            <Form.Select value={yearId} onChange={(e) => setYearId(e.target.value)}>
              <option value="">-- Select --</option>
              {intakes.map((i) => <option key={i.yearId} value={i.yearId}>{i.yearName}</option>)}
            </Form.Select>
          </Form.Group>
        )}
        <Form.Group className="mb-3">
          <Form.Label>Course *</Form.Label>
          <Form.Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">-- Select --</option>
            {availableCourses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Form.Select>
          {availableCourses.length === 0 && (
            <div className="pm-cell-muted" style={{ fontSize: 12, marginTop: 6 }}>
              All courses are already added to this intake.
            </div>
          )}
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Term</Form.Label>
          <Form.Select value={termId} onChange={(e) => setTermId(e.target.value)}>
            <option value="">-- None --</option>
            {terms.map((t) => <option key={t.id} value={t.id}>{t.displayName || t.name}</option>)}
          </Form.Select>
        </Form.Group>
        <div className="row">
          <div className="col-4">
            <Form.Group className="mb-3">
              <Form.Label>Credits</Form.Label>
              <Form.Control type="number" min="0" step="0.5" value={credits} onChange={(e) => setCredits(e.target.value)} />
            </Form.Group>
          </div>
          <div className="col-4">
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Form.Group>
          </div>
          <div className="col-4">
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Form.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button
          variant="primary"
          disabled={!courseId || !yearId}
          onClick={() => onSubmit({
            courseId: Number(courseId),
            yearId: Number(yearId),
            termId: termId ? Number(termId) : null,
            credits: credits ? Number(credits) : null,
            startDate: startDate || null,
            endDate: endDate || null,
          })}
        >
          Add Module
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProgramViewPage;
